/**
 * Player nicknames: one per wallet address, shown in game, in the kill feed, in the lobby and on the
 * leaderboards instead of "0x1a5f…9f3a". Validated the same way on the client (instant feedback) and
 * on the server (authoritative).
 */
export const NICKNAME = { minLength: 3, maxLength: 16 } as const;

// Words that may not appear anywhere in a name, after folding look-alike characters (0→o, 1→i, $→s…)
// and dropping separators, so "n_1_g" style tricks are caught too. Kept short on purpose: slurs and
// the most common profanity; moderators can still rename anyone through the database.
const BLOCKED = [
    "nigger", "nigga", "niga", "faggot", "fagot", "retard", "tranny", "kike", "spic", "chink", "gook", "wetback", "coon",
    "hitler", "nazi", "heil", "kkk", "rape", "rapist", "pedo", "pedophile", "molest",
    "fuck", "fuk", "shit", "cunt", "bitch", "whore", "slut", "dick", "cock", "pussy", "penis", "vagina", "porn", "sex",
    "asshole", "bastard", "wanker", "twat", "cum", "jizz", "anal"
];
// names that would look official or impersonate the game's own labels: the whole name, or one word of it ("Dev Bob",
// "Bob_Staff", "TheMod")
const RESERVED = ["admin", "moderator", "mod", "developer", "dev", "staff", "system", "server", "trenchio", "official", "support"];
// these can't start or end a name either ("AdminBob", "Administrator", "bob4admin"), and the game's name can't be in it at
// all; the short ones only count as a word of their own ("Devon", "Modric", "Observer" are fine)
const RESERVED_AFFIXES = ["admin", "moderator", "developer", "official", "support"];
const BRAND = "trenchio";

const FOLD: Readonly<Record<string, string>> = { 0: "o", 1: "i", 3: "e", 4: "a", 5: "s", 6: "g", 7: "t", 8: "b", 9: "g", "@": "a", $: "s", "!": "i", "|": "i" };

/** lower-case, look-alikes folded, separators removed: used for filtering and for uniqueness */
export function nicknameKey(name: string): string {
    return name.toLowerCase().split("").map(ch => FOLD[ch] ?? ch).join("").replace(/[^a-z]/g, "");
}

/** a name's words, each folded like `nicknameKey`: split at spaces, _ . - and where the case changes ("TheMod" → the, mod) */
function nicknameWords(name: string): string[] {
    return name
        .split(/[\s_.-]+|(?<=[a-z0-9])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/u)
        .map(nicknameKey)
        .filter(word => word.length > 0);
}

/** whether a text has a slur or a swear word in it, folded as names are (a flea market listing's name, 0.98) */
export function hasBlockedWord(text: string): boolean {
    const key = nicknameKey(text);
    return BLOCKED.some(word => key.includes(word));
}

export type NicknameCheck = { readonly ok: true, readonly name: string } | { readonly ok: false, readonly error: string };

export function checkNickname(raw: unknown): NicknameCheck {
    if (typeof raw !== "string") return { ok: false, error: "Type a name" };
    const name = raw.replace(/\s+/g, " ").trim();
    if (name.length < NICKNAME.minLength) return { ok: false, error: `At least ${NICKNAME.minLength} characters` };
    if (name.length > NICKNAME.maxLength) return { ok: false, error: `At most ${NICKNAME.maxLength} characters` };
    if (!/^[A-Za-z0-9 _.-]+$/.test(name)) return { ok: false, error: "Letters, numbers, spaces and _ . - only" };
    if ((name.match(/[A-Za-z]/g) ?? []).length < 2) return { ok: false, error: "Use at least two letters" };
    // an address, or the way the game shows one ("0x1a5f…9f3a"): with an O for the zero too ("Ox1a5f...9f3a")
    if (/^0x[0-9a-f]/i.test(name) || /^[0o]x[0-9a-f]{2,}(?:[\s_.-]*[0-9a-f]+)*$/i.test(name)) {
        return { ok: false, error: "Names can't look like an address" };
    }
    if (/^bot[\s_.-]*\d/i.test(name)) return { ok: false, error: "Names can't look like a bot" };
    const key = nicknameKey(name);
    // guests are Guest-1234: a chosen name can't pass for one
    if (key.startsWith("guest")) return { ok: false, error: "Names starting with Guest are kept for guests" };
    if (
        RESERVED.includes(key) ||
        nicknameWords(name).some(word => RESERVED.includes(word)) ||
        RESERVED_AFFIXES.some(word => key.startsWith(word) || key.endsWith(word)) ||
        key.includes(BRAND)
    ) {
        return { ok: false, error: "That name is reserved" };
    }
    if (BLOCKED.some(word => key.includes(word))) return { ok: false, error: "Pick a different name" };
    return { ok: true, name };
}
